import { messages, type MessageKey } from "./translations";

export const LOCALES = ["en", "fi", "ru", "uk"] as const;
export type Locale = (typeof LOCALES)[number];
export const LANGUAGE_NAMES: Record<Locale, string> = {
  en: "English",
  fi: "Suomi",
  ru: "Русский",
  uk: "Українська",
};
export const INTL_LOCALES: Record<Locale, string> = {
  en: "en-GB",
  fi: "fi-FI",
  ru: "ru-RU",
  uk: "uk-UA",
};
export const LANGUAGE_KEY = "luuta.analytics.language";
type Parameters = Record<string, string | number>;
export type PreferenceStorage = Pick<Storage, "getItem" | "setItem">;
export const isLocale = (value: unknown): value is Locale =>
  LOCALES.includes(value as Locale);
export function readLocale(storage?: Pick<Storage, "getItem">): Locale {
  try {
    const value = (storage ?? globalThis.localStorage)?.getItem(LANGUAGE_KEY);
    return isLocale(value) ? value : "en";
  } catch {
    return "en";
  }
}
let locale: Locale = readLocale();
const listeners = new Set<() => void>();
export const getLocale = () => locale;
export const subscribeLocale = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export function setLocale(next: Locale, storage?: Pick<Storage, "setItem">) {
  if (!isLocale(next)) return;
  locale = next;
  try {
    (storage ?? globalThis.localStorage)?.setItem(LANGUAGE_KEY, next);
  } catch {
    /* The selection remains available in this session. */
  }
  listeners.forEach((listener) => listener());
}
export function translate(
  key: MessageKey,
  selected: Locale,
  params: Parameters = {},
): string {
  const entry = messages[key];
  const text = selected === "en" ? key : entry[LOCALES.indexOf(selected) - 1];
  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    String(params[name] ?? match),
  );
}
/** Translate app-owned text only. Unknown technical diagnostics remain verbatim. */
export function tx(
  text: string,
  params: Parameters = {},
  selected = getLocale(),
): string {
  const key = Object.hasOwn(messages, text)
    ? text
    : text.trim().replace(/\s+/g, " ");
  if (Object.hasOwn(messages, key)) {
    const translated = translate(key as MessageKey, selected, params);
    return (
      text.slice(0, text.length - text.trimStart().length) +
      translated +
      text.slice(text.trimEnd().length)
    );
  }
  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    String(params[name] ?? match),
  );
}
export function countText(
  kind: "records" | "rows" | "reports",
  count: number,
  selected = getLocale(),
) {
  const category = new Intl.PluralRules(INTL_LOCALES[selected]).select(count);
  const forms = {
    en: {
      records: ["record", "records", "records"],
      rows: ["row", "rows", "rows"],
      reports: ["report", "reports", "reports"],
    },
    fi: {
      records: ["tietue", "tietuetta", "tietuetta"],
      rows: ["rivi", "riviä", "riviä"],
      reports: ["raportti", "raporttia", "raporttia"],
    },
    ru: {
      records: ["запись", "записи", "записей"],
      rows: ["строка", "строки", "строк"],
      reports: ["отчёт", "отчёта", "отчётов"],
    },
    uk: {
      records: ["запис", "записи", "записів"],
      rows: ["рядок", "рядки", "рядків"],
      reports: ["звіт", "звіти", "звітів"],
    },
  };
  const form = category === "one" ? 0 : category === "few" ? 1 : 2;
  return `${new Intl.NumberFormat(INTL_LOCALES[selected]).format(count)} ${forms[selected][kind][form]}`;
}

/** Stable app errors are translated at presentation time, including errors received from workers. */
export function errorText(message: string) {
  const unknown = message.match(/^Unknown field: (.*)$/);
  return unknown
    ? tx("Unknown field: {field}", { field: unknown[1] })
    : tx(message);
}
export function editorPhrases(selected: Locale) {
  return Object.fromEntries(
    Object.keys(messages).map((key) => [key, tx(key, {}, selected)]),
  );
}
